/**
 * Locale pick and dismiss rules for the panel status banner.
 *
 * The API already filtered to live + matching notices. What remains is
 * presentation: which language to show, and whether this browser has hidden
 * a given strip. Those decisions stay here so the component can stay small.
 *
 * Dismiss is local to this browser on purpose — there is no email blast and
 * no server-side acknowledgement. Storage is keyed per signed-in user so a
 * shared profile or an account switch cannot hide a strip from the next
 * person. Two rules, documented on the admin page:
 *
 *   until_updated  info / warning. Hidden until the notice is edited.
 *   for_24h        maintenance / outage. Hidden for a day, then it returns.
 */
import type { StatusNotice } from '../api/types';

export type PanelLocale = 'en' | 'fi';

const STORAGE_PREFIX = 'vgncloud.statusNotice.dismiss.v1';
const DAY_MS = 24 * 60 * 60 * 1000;

export function dismissStorageKey(userId: number | string | null | undefined): string {
  if (userId === null || userId === undefined || userId === '') return STORAGE_PREFIX;
  return `${STORAGE_PREFIX}.${userId}`;
}

interface DismissRecord {
  /** Notice.updated_at at the moment it was hidden. */
  updatedAt: string;
  /** Epoch ms after which the hide expires. */
  until: number;
}

type DismissMap = Record<string, DismissRecord>;

export function panelLocale(language: string = typeof navigator !== 'undefined' ? navigator.language : 'en'): PanelLocale {
  return language.toLowerCase().startsWith('fi') ? 'fi' : 'en';
}

export function noticeTitle(notice: StatusNotice, locale: PanelLocale): string {
  const primary = locale === 'fi' ? notice.title_fi : notice.title_en;
  const fallback = locale === 'fi' ? notice.title_en : notice.title_fi;
  const chosen = primary.trim() || fallback.trim();
  return chosen;
}

export function noticeBody(notice: StatusNotice, locale: PanelLocale): string | null {
  const primary = locale === 'fi' ? notice.body_fi : notice.body_en;
  const fallback = locale === 'fi' ? notice.body_en : notice.body_fi;
  const chosen = (primary ?? '').trim() || (fallback ?? '').trim();
  return chosen === '' ? null : chosen;
}

export function bannerCopy(locale: PanelLocale): {
  created: string;
  updated: string;
  dismiss: string;
} {
  if (locale === 'fi') {
    return { created: 'Luotu', updated: 'Päivitetty viimeksi', dismiss: 'Sulje ilmoitus' };
  }
  return { created: 'Created', updated: 'Last updated', dismiss: 'Dismiss notice' };
}

function readMap(userId?: number | string | null): DismissMap {
  try {
    const raw = localStorage.getItem(dismissStorageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    return parsed as DismissMap;
  } catch {
    return {};
  }
}

function writeMap(map: DismissMap, userId?: number | string | null): void {
  try {
    localStorage.setItem(dismissStorageKey(userId), JSON.stringify(map));
  } catch {
    // Private mode / quota — the strip simply cannot remember a dismiss.
  }
}

export function isNoticeDismissed(
  notice: StatusNotice,
  now: number = Date.now(),
  userId?: number | string | null,
): boolean {
  const record = readMap(userId)[String(notice.id)];
  if (!record) return false;
  if (record.updatedAt !== notice.updated_at) return false;
  return record.until > now;
}

export function dismissNotice(
  notice: StatusNotice,
  now: number = Date.now(),
  userId?: number | string | null,
): void {
  const map = readMap(userId);
  const until = notice.dismiss === 'for_24h' ? now + DAY_MS : now + 10 * 365 * DAY_MS;
  map[String(notice.id)] = { updatedAt: notice.updated_at, until };
  writeMap(map, userId);
}

export function visibleNotices(
  notices: readonly StatusNotice[],
  now: number = Date.now(),
  userId?: number | string | null,
): StatusNotice[] {
  return notices.filter((notice) => !isNoticeDismissed(notice, now, userId));
}
